/**
 * HTTP layer for operational reports. The compliance/maintenance report feed:
 * list, detail, summary rollup, and CSV export.
 *
 * Contains:
 * - fetchOperationalReports / fetchOperationalReport: list and detail reads
 * - fetchOperationalReportsSummary: summary rollup
 * - exportOperationalReportsToCSV: CSV export
 */
import apiClient from '@sophocles/http-common';

/**
 * Fetch operational reports with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.site - Filter by site ID
 * @param {string} params.site_name - Filter by site name (case-insensitive contains)
 * @param {Array<string>} params.status - Filter by status array (passed|failed|error|skipped|pending)
 * @param {string} params.validation_mode - Filter by validation mode
 * @param {string} params.report_date - Exact date (YYYY-MM-DD)
 * @param {string} params.report_date_after - Reports on or after this date
 * @param {string} params.report_date_before - Reports on or before this date
 * @param {string} params.date_range_start - Alias for report_date_after
 * @param {string} params.date_range_end - Alias for report_date_before
 * @param {string} params.o - Ordering field (e.g. 'report_date' or '-report_date')
 * @param {number} params.limit - Number of results per page
 * @param {number} params.offset - Offset for pagination
 * @returns {Promise} API response with results array and count
 */
export const fetchOperationalReports = params =>
  apiClient.get('/operational-reports/', { params }).then(res => res.data);

/**
 * Fetch a single operational report by ID
 * @param {string} reportId - UUID of the report
 * @returns {Promise} API response with report details
 */
export const fetchOperationalReport = reportId =>
  apiClient.get(`/operational-reports/${reportId}/`).then(res => res.data);

/**
 * Fetch operational reports summary/statistics
 * This is a helper for dashboard views to get aggregated data
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} API response with summary statistics
 */
export const fetchOperationalReportsSummary = params => {
  // This will fetch reports and calculate stats on the frontend
  // Could be enhanced with a backend endpoint for better performance
  return fetchOperationalReports({ ...params, limit: 1000 }).then(response => {
    const { results = [], count = 0 } = response;

    const summary = {
      total: count,
      pending: 0,
      passed: 0,
      failed: 0,
      error: 0,
      skipped: 0,
      passRate: 0,
      failureReasons: {
        no_communication: 0,
        alarms_during_operation: 0,
      },
    };

    results.forEach(report => {
      const status = report.status?.toLowerCase();
      if (Object.prototype.hasOwnProperty.call(summary, status)) {
        summary[status]++;
      }

      // Track failure reasons
      if (status === 'failed') {
        const failureType = report.data?.failure_type;
        if (failureType && Object.prototype.hasOwnProperty.call(summary.failureReasons, failureType)) {
          summary.failureReasons[failureType]++;
        }
      }
    });

    // Calculate pass rate (excluding pending/error/skipped)
    const completed = summary.passed + summary.failed;
    if (completed > 0) {
      summary.passRate = ((summary.passed / completed) * 100).toFixed(2);
    }

    return summary;
  });
};

/**
 * Export operational reports to CSV
 * @param {Object} params - Query parameters (same as fetchOperationalReports)
 * @returns {Promise} Downloads CSV file
 */
export const exportOperationalReportsToCSV = params => {
  return apiClient
    .get('/operational-reports/export-csv/', {
      params,
      responseType: 'blob', // Important for file downloads
    })
    .then(response => {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'operational_reports.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return response;
    });
};

export default {
  fetchOperationalReports,
  fetchOperationalReport,
  fetchOperationalReportsSummary,
  exportOperationalReportsToCSV,
};
